import type { AxiosError, AxiosResponse } from 'axios'
import type { Problem } from '@/types/generated'
import { isAxiosError } from 'axios'

export type AppProblem = Problem & { [extension: string]: unknown }

export const PROBLEM_TYPES = {
  blank: 'about:blank',
  network: 'https://artoffiber.com/problems/network',
  canceled: 'https://artoffiber.com/problems/request-canceled',
  unknown: 'https://artoffiber.com/problems/unknown',
} as const

const AMBIENT_STATUSES = new Set([401, 408, 419, 429])

const PROBLEM_TIER_OVERRIDES: Record<string, boolean> = {
  [PROBLEM_TYPES.canceled]: false,
}

const problemCache = new WeakMap<object, AppProblem>()
const notifiedProblems = new WeakSet<AppProblem>()

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isObjectError(error: unknown): error is object {
  return typeof error === 'object' && error !== null
}

function hasProblemContentType(response: AxiosResponse): boolean {
  const contentType = response.headers?.['content-type']
  return typeof contentType === 'string' && contentType.includes('application/problem+json')
}

function looksLikeProblemBody(data: unknown): boolean {
  return isRecord(data)
    && typeof data.type === 'string'
    && typeof data.status === 'number'
    && typeof data.title === 'string'
}

function legacyDetail(data: unknown): string | undefined {
  if (typeof data === 'string') {
    return data
  }

  if (isRecord(data)) {
    if (typeof data.message === 'string') {
      return data.message
    }
    if (typeof data.error === 'string') {
      return data.error
    }
  }

  return undefined
}

function legacyErrors(data: unknown): Record<string, string[]> | undefined {
  if (isRecord(data) && isRecord(data.errors)) {
    return data.errors as Record<string, string[]>
  }

  return undefined
}

function spreadProblemBody(status: number, data: unknown): AppProblem {
  const record = isRecord(data) ? data : {}
  return { type: PROBLEM_TYPES.blank, status, title: '', ...record } as AppProblem
}

function toProblemFromAxiosError(error: AxiosError): AppProblem {
  if (error.code === 'ERR_CANCELED') {
    return attachProblem(error, { type: PROBLEM_TYPES.canceled, status: 0, title: '' })
  }

  const response = error.response
  if (!response) {
    return attachProblem(error, {
      type: PROBLEM_TYPES.network,
      status: 0,
      title: '',
      ...(error.code ? { code: error.code } : {}),
    })
  }

  const data = response.data

  if (hasProblemContentType(response) || looksLikeProblemBody(data)) {
    return attachProblem(error, spreadProblemBody(response.status, data))
  }

  const detail = legacyDetail(data)
  const errors = legacyErrors(data)

  return attachProblem(error, {
    type: PROBLEM_TYPES.blank,
    status: response.status,
    title: '',
    ...(detail !== undefined ? { detail } : {}),
    ...(errors ? { errors } : {}),
  })
}

export function isProblem(value: unknown): value is AppProblem {
  return isRecord(value)
    && typeof value.type === 'string'
    && typeof value.status === 'number'
    && typeof value.title === 'string'
}

export function attachProblem(error: object, problem: AppProblem): AppProblem {
  problemCache.set(error, problem)
  return problem
}

export function markProblemNotified(problem: AppProblem): void {
  notifiedProblems.add(problem)
}

export function wasProblemNotified(problem: AppProblem): boolean {
  return notifiedProblems.has(problem)
}

export function toProblem(error: unknown): AppProblem {
  if (isObjectError(error)) {
    const cached = problemCache.get(error)
    if (cached) {
      return cached
    }
  }

  if (isProblem(error)) {
    return error
  }

  if (isAxiosError(error)) {
    return toProblemFromAxiosError(error)
  }

  if (error instanceof Error) {
    return { type: PROBLEM_TYPES.unknown, status: 0, title: '', detail: error.message }
  }

  return { type: PROBLEM_TYPES.unknown, status: 0, title: '' }
}

export function problemMessage(problem: AppProblem, fallback: string): string {
  const firstBagEntry = problem.errors ? Object.values(problem.errors).flat()[0] : undefined
  return firstBagEntry || problem.detail || problem.title || fallback
}

/**
 * The failure a caller still has to notify: never one the interceptor already notified, nor a canceled request.
 * An HTTP problem wins over a client-side error, whose generic fallback would otherwise hide a 403 or 422.
 */
export function problemToNotify(errors: readonly unknown[]): AppProblem | undefined {
  const pending = errors
    .map(toProblem)
    .filter(problem => !wasProblemNotified(problem) && problem.type !== PROBLEM_TYPES.canceled)

  return pending.find(problem => problem.status > 0) ?? pending[0]
}

export function isAmbientProblem(problem: AppProblem): boolean {
  return PROBLEM_TIER_OVERRIDES[problem.type] ?? (problem.status === 0 || problem.status >= 500 || AMBIENT_STATUSES.has(problem.status))
}
